import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { alpha, makeStyles } from "@material-ui/core/styles";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { resetOrgData, logoutFromBlumira } from "../redux/actions/Page";

import {
  Badge,
  Avatar,
  Button,
  Select,
  Tooltip,
  Toolbar,
  MenuItem,
  IconButton,
  FormControl,
} from "@material-ui/core";

import { WILDCARD_ORG_ID } from "../lib/api/API";
import logo from "../images/blumira-logo-white.svg";

import TopbarView from "./TopbarView";
import SearchableListDialog from "./Components/SearchableListDialog";

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    margin: theme.spacing(1),
  },
  backButton: {
    boxShadow: "none",
    marginLeft: theme.shape.padding,
    border: `1px solid ${theme.palette.common.white}`,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
  },
  keyboardBackspaceIcon: {
    paddingRight: 5,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
    }),
  },
  toolbarActions: {
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
  },
  userAvatar: {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  userLogout: {
    margin: theme.spacing(1),
  },
  topbarNav: {
    flexGrow: 1,
  },
  orgDialogButton: {
    padding: 10,
    minWidth: 200,
    cursor: "pointer",
    position: "relative",
    margin: theme.spacing(1),
    border: "1px solid lightgray",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "& p": {
      margin: 0,
    },
  },
}));

export const ADMIN_MODE_ORG = {
  id: WILDCARD_ORG_ID,
  name: "Administer All Orgs",
};

const setRoute = (currentOrg, payload) => {
  const { toplevel, secondlevel } = payload;

  // to account for administering all orgs for SAs
  if (!currentOrg) return { toplevel, secondlevel };

  const shouldRedirectToDashboard =
    !(currentOrg.market === 20 && currentOrg.parentId === null) &&
    payload.toplevel === "mspportal";

  if (shouldRedirectToDashboard) {
    return { toplevel: "dashboard", secondlevel: "summary" };
  }

  return { toplevel, secondlevel };
};

const HeaderView = (props) => {
  const classes = useStyles();

  const [orgList, setOrgList] = useState([]);
  const [currentParentId, setCurrentParentId] = useState(null);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [showGoToPortalButton, setShowGoToPortalButton] = useState(false);

  const {
    first_name = "",
    last_name = "",
    picture = "",
    superadmin = false,
    orgRoles = [],
  } = props.session.settings.user;

  useEffect(() => {
    const newOrgList = superadmin
      ? [ADMIN_MODE_ORG, ...props.session.settings.userOrgs]
      : props.session.settings.userOrgs;
    setOrgList(newOrgList);

    if (props.session?.settings?.userOrgs) {
      // find current org to determine if child
      const currentOrg = props.session.settings.userOrgs.find(
        ({ id }) => id === props.orgId
      );
      let parentId = currentOrg ? currentOrg.parentId : null;

      // if the current org is a child account
      // find the user's role in parent account
      if (parentId) {
        setCurrentParentId(parentId);
        // get the current user's roles in parent account
        const parentRoles = orgRoles.filter(({ orgId }) => orgId === parentId);

        // if current user is viewing a child account and
        // is assigned 'administrator' in the parent account
        // display 'Go to MSP Portal' button
        const isAdministratorInParentOrg = parentRoles.some(
          ({ roleId }) => roleId === 10
        );
        if (!!isAdministratorInParentOrg || superadmin)
          setShowGoToPortalButton(true);
        else setShowGoToPortalButton(false);
      } else {
        setShowGoToPortalButton(false);
      }
    }
  }, [
    props.session,
    props.session.settings.userOrgs,
    superadmin,
    props.orgId,
    orgRoles,
  ]);

  const fullName = `${first_name || ""} ${last_name || ""}`;

  const routeToOrg = (orgId) => {
    const { routeToOrg, type, query = {}, session, payload } = props;
    const { userOrgs } = session.settings;
    const currentOrg = userOrgs.find((obj) => obj.id === orgId);

    const newPayload = setRoute(currentOrg, payload);
    routeToOrg({
      type,
      payload: { ...newPayload, orgId },
      query: { ...query },
    });
  };

  const handleNotificationClick = () => {
    props.dispatch({
      type: "PAGE",
      payload: {
        orgId: props.payload.orgId,
        toplevel: "user",
        secondlevel: "alertsettings",
      },
    });
  };

  const handleUserClick = () => {
    props.dispatch({
      type: "PAGE",
      payload: {
        orgId: props.payload.orgId,
        toplevel: "user",
        secondlevel: "myaccount",
      },
    });
  };

  const toggleOrgDialog = () => {
    setIsOrgDialogOpen(!isOrgDialogOpen);
  };

  const handleBackToPortal = () => {
    const { routeToOrg } = props;

    routeToOrg({
      type: "PAGE",
      payload: {
        toplevel: "mspportal",
        orgId: currentParentId,
        secondlevel: "accounts",
      },
    });
  };

  return (
    <Toolbar>
      <div>
        <img style={{ height: "32px" }} src={logo} alt="Logo" />
        {showGoToPortalButton && (
          <Button
            className={classes.backButton}
            variant={"contained"}
            color={"primary"}
            onClick={handleBackToPortal}
          >
            Go to MSP Portal
          </Button>
        )}
      </div>
      <div className={classes.topbarNav}>
        {props.withNav && (
          <TopbarView
            toplevel={props.payload.toplevel}
            secondlevel={props.payload.secondlevel}
            dispatch={props.dispatch}
            orgId={props.payload.orgId}
          />
        )}
      </div>

      {orgList.length > 0 && orgList.length <= 5 ? (
        <FormControl className={classes.formControl}>
          <Select
            className={classes.inputRoot}
            align="center"
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
            value={props.payload.orgId}
            onChange={(e) => routeToOrg(e.target.value)}
            label="Organization"
            data-cy={"selectOrgBtn"}
          >
            {orgList
              .sort((a, b) =>
                a.name.localeCompare(b.name, "en", { sensitivity: "base" })
              )
              .map((org) => (
                <MenuItem key={org.id} value={org.id} data-cy={"selectOrgItem"}>
                  {org.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      ) : orgList.length > 5 ? (
        <div
          className={classes.orgDialogButton}
          onClick={toggleOrgDialog}
          data-cy={"selectOrgBtn"}
        >
          <p>
            {orgList.find(({ id }) => id === props.payload.orgId).name || ""}
          </p>
        </div>
      ) : null}

      <IconButton
        data-cy={"notificationSettingsBtn"}
        className={classes.toolbarActions}
        onClick={handleNotificationClick}
      >
        <Tooltip title="Notification Settings" overlap={"rectangular"}>
          <Badge badgeContent={0} color="secondary">
            <NotificationsIcon />
          </Badge>
        </Tooltip>
      </IconButton>

      <IconButton
        data-cy={"accountSettingsBtn"}
        className={classes.toolbarActions}
        onClick={handleUserClick}
      >
        <Tooltip title="Account Settings">
          <Avatar alt={fullName} src={picture} className={classes.userAvatar} />
        </Tooltip>
      </IconButton>

      <IconButton
        data-cy={"logoutBtn"}
        className={classes.toolbarActions}
        onClick={props.routeToLogin}
      >
        <Tooltip title="Logout">
          <ExitToAppIcon />
        </Tooltip>
      </IconButton>
      <SearchableListDialog
        open={isOrgDialogOpen}
        onItemClick={routeToOrg}
        onClose={toggleOrgDialog}
        title={"Switch organizations"}
        items={orgList.sort((a, b) =>
          a.name.localeCompare(b.name, "en", { sensitivity: "base" })
        )}
      />
    </Toolbar>
  );
};

HeaderView.propTypes = {
  payload: PropTypes.shape({}),
  query: PropTypes.shape({}),
  routeToLogin: PropTypes.func.isRequired,
  routeToOrg: PropTypes.func.isRequired,
};

HeaderView.defaultProps = {
  payload: {},
  query: {},
};

const mapDispatchToProps = (dispatch, getState) => ({
  routeToLogin: () => {
    dispatch(logoutFromBlumira);
  },
  routeToOrg: (props) => {
    dispatch(resetOrgData);
    dispatch({ type: "PAGE", payload: props.payload });
  },
});

const mapStateToProps = (state) => {
  const { session = {} } = state;
  return {
    orgId: state.page.payload.orgId,
    payload: state.location.payload,
    session: session,
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(HeaderView);
